import router from '@/router';
import { ServicesContainer } from '@/services';
import { ProductViewEvent } from '@/services/analytics/events/';
import { AnalyticsEventType, ChipotleTenant, WebPageDetails } from '@/services/analytics/platforms/adobe-analytics/models';
import { AppState } from '@/store';
import { Store } from 'vuex';
import { AdobePageLoadEvent } from './adobe-page-load';
import { BYOMealType, MealType, PreConfiguredMeal } from '@/models';
import { slugify } from '@/helpers';

export class AdobeProductViewEvent extends AdobePageLoadEvent {
  _chipotle? : ChipotleTenant

  event = AnalyticsEventType.ProductViews;

  constructor(event: ProductViewEvent, store: Store<AppState>, services: ServicesContainer) {
    super(store, services);
    
    const groupName = router.currentRoute.params.groupName;

    Object.assign(this.web.webPageDetails, this.getMealBuilderWebPageDetails(groupName, store));
    this.web.webPageDetails.pageViews = { value: 1 };
    this.commerce.productViews = { value: 1 };
    
    if (event.isMealBuilderMobileCustomizationsExperienceSubscribed && event.isWebMobile){
      this._chipotle = { pas: { 
        experienceDisplay : { value: 1},
        experienceID: store.state.adobeTarget.mealBuilderMobileCustomizationsExperienceData?.experienceID,
        experienceName: store.state.adobeTarget.mealBuilderMobileCustomizationsExperienceData?.experienceName,
        experienceSource: store.state.adobeTarget.mealBuilderMobileCustomizationsExperienceData?.experienceSource
      } }
    }

    if (groupName == 'lifestyle-bowl' || groupName == `kid's-meal`) {
      this.commerce.menuListViews = { value: 1 };
    }

    if (groupName != 'lifestyle-bowl' && groupName != `kid's-meal` && !event.isEditMode) {
      this.commerce.startBuild = { value: 1 };
    }

    if (store.state.restaurant?.selectedRestaurant?.restaurantNumber) {
      this.commerce.localizedBuilderView = { value: 1 };
    } else {
      this.commerce.universalBuilderView = { value: 1 };
    }

    if (store.state.adobe.isMakeItMyOwn) {
      this.commerce.pceMakeItMyOwn = { value: 1 };
      services.utils.setIsMakeItMyOwn(false);
    }
  }

  getMealBuilderWebPageDetails(groupName: string, store: Store<AppState>): WebPageDetails {
    const pcmId = router.currentRoute.params.pcmId;
    const pceDetails = this.getPCEDetails(pcmId, store);

    if (pceDetails) {
      return this.createPCEMealBuilderDetails(pceDetails.mealType, pceDetails.name);
    } else {
      return this.createMealBuilderDetails(groupName, store);
    }
  }

  createMealBuilderDetails(groupName: string, store: Store<AppState>): WebPageDetails {
    const siteSection = 'order';
    const subSection1 = router.currentRoute.name;
    const subSection2 =
      groupName == `kid's-quesadilla` || groupName == `kid's-build-your-own`
        ? `kid's-meal`
        : '';
    const page = groupName;

    let details = {
      pageType: router.currentRoute.name,
      siteSection,
      subSection1,
      subSection2,
      page,
      name: `${siteSection}:${subSection1}:${subSection2 && subSection2 + ':'}${page}`
    } as WebPageDetails;

    return details;
  }

  createPCEMealBuilderDetails(mealType: MealType, name: string) {

    let webPageDetails = {
      pageType: 'meal-builder',
      siteSection: 'order',
      subSection1: 'meal-builder',
      subSection2: '',
      name: name
    } as WebPageDetails

    if (mealType == MealType.BuildYourOwn) {
      webPageDetails.page = 'byoc'
    }

    return webPageDetails;
  }

  private getPCEDetails(pcmId: string, store: Store<AppState>) {
    const pce = store.state.menu.preConfiguredMeals.find(pcm => pcmId === pcm.mealId);

    if (pce?.mealType == MealType.BuildYourOwn) {
      const byocMealType = store.state.menu.menuItemLookupMap?.get(pce?.entree.itemId)?.menuItemType;
      return { mealType: MealType.BuildYourOwn, name: `order:meal-builder:${ byocMealType === BYOMealType.BYOProtein ? 'pce-byoc-meal' : 'pce-byoc-chips-and-dip'}` }
    }

    if (pce?.mealType == MealType.Lifestyle) {
      return { mealType: MealType.Lifestyle, name: `order:meal-builder:pce-lifestyle-bowl-${slugify(pce.mealName)}` }
    }

    if (pce?.mealType == MealType.Influencer) {
      return { mealType: MealType.Lifestyle, name: `order:meal-builder:pce-featured-meals-${slugify(pce.mealName)}` }
    }

    if (pce?.mealType == MealType.HighProtein) {
      return { mealType: MealType.HighProtein, name: `order:meal-builder:pce-high-protein-meals-${slugify(pce.mealName)}` }
    }
  }
}
